import { PLATFORM } from 'aurelia-pal';
/**
 * A set of generic utilities for various purposes.
 */
class AppUtils {
  /**
   * Adds an event listener to the window.
   * @param {string|Array} events The name of one or more events.
   * @param {Function}     fn     The function to be triggered when the events are
   *                              triggered.
   * @param {...*}         rest   Extra parameters to include on the call to
   *                              `addEventListener`.
   * @return {Subscription}
   */
  attachWindowEvents(events, fn, ...rest) {
    const list = this.ensureArray(events);
    list.forEach((event) => PLATFORM.global.addEventListener(...[event, fn, ...rest]));
    return {
      dispose: () => this.detachWindowEvents(list, fn, ...rest),
    };
  }
  /**
   * Removes an event listener from the window.
   * @param {string|Array} events The name of one or more events.
   * @param {Function}     fn     The listener function.
   * @param {...*}         rest   Extra parameters to include on the call to
   *                              `removeEventListener`.
   */
  detachWindowEvents(events, fn, ...rest) {
    const list = this.ensureArray(events);
    list.forEach((event) => PLATFORM.global.removeEventListener(...[event, fn, ...rest]));
  }
  /**
   * Ensures an object is an array.
   * @param {*} obj The object to check.
   * @return {Array}
   */
  ensureArray(obj) {
    return Array.isArray(obj) ? obj : [obj];
  }
  /**
   * Returns the value of an object property using a path.
   * @example
   * const obj = {
   *   propOne: {
   *     propOneSub: 'Charito!',
   *   },
   *   propTwo: '!!!',
   * };
   * console.log(Utils.getPropertyWithPath(
   *   obj,
   *   'propOne.propOneSub'
   * ));
   * // Will output `Charito!`
   *
   * @param {Object} obj                 The object from where the property will be read.
   * @param {string} objPath             The path to the property.
   * @param {string} [pathDelimiter='/'] The delimiter that will separate the path components.
   * @return {*}
   * @throws {Error} If the path is invalid.
   */
  getPropertyWithPath(obj, objPath, pathDelimiter = '.') {
    const parts = objPath.split(pathDelimiter);
    const first = parts.shift();
    let currentElement = obj[first];
    if (typeof currentElement === 'undefined') {
      throw new Error(`There's nothing on '${objPath}'`);
    } else if (parts.length) {
      let currentPath = first;
      parts.forEach((currentPart) => {
        currentPath += `/${currentPart}`;
        currentElement = currentElement[currentPart];
        if (typeof currentElement === 'undefined') {
          throw new Error(`There's nothing on '${currentPath}'`);
        }
      });
    }

    return currentElement;
  }
  /**
   * Scrolls the browser to the top of the page.
   */
  scrollToTop() {
    PLATFORM.global.scrollTo(0, 0);
  }
  /**
   * This method is used for sorting an array of objects by a property of those objects. The
   * returned value of this method should be used insde a `.sort` call.
   * @example
   * [
   *     { name: 'Nightwing' },
   *     { name: 'Red Hood'},
   *     { name: 'Batman'}
   * ].sort(sortObjectsList('name'));
   * // The order would now be 'Batman', 'Nightwing' and 'Red Hood'
   *
   * @param {string} property      The name of the objects property you want to sort the array
   *                               with.
   * @param {string} [order='asc'] String representing the sort order, 'asc' or 'desc'
   * @return {Function}
   */
  sortObjectsList(property, order = 'asc') {
    const orderN = order === 'asc' ? 1 : -1;
    return (a, b) => {
      const propA = a[property];
      const propB = b[property];
      let result = 0;
      if (propA > propB) {
        result = 1 * orderN;
      } else if (propB > propA) {
        result = -1 * orderN;
      }

      return result;
    };
  }
}

export { AppUtils };
